/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import DataTable from "examples/Tables/DataTable";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// Data

import { useState, useEffect, useRef, useLayoutEffect } from "react";
import axios from "axios";
import { API_HOHOLOK } from "utils";
import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";

function formatTanggalIndo(dateStr) {
  if (!dateStr) return "-";
  const bulan = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return `${d.getDate()} ${bulan[d.getMonth()]} ${d.getFullYear()}`;
}

// Format tanggal pendek dd/mm/yyyy
function formatTanggalShort(dateObj) {
  const d = new Date(dateObj);
  if (isNaN(d)) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function KurvaTargetRealisasi() {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState(null);
  const [proyekList, setProyekList] = useState([]);
  const [selectedProyek, setSelectedProyek] = useState(null);
  const [tahun, setTahun] = useState(2025);
  const [loading, setLoading] = useState(false);
  const [id_perusahaan, setIdPerusahaan] = useState(null);
  const [status, setStatus] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [weeklyTable, setWeeklyTable] = useState({ columns: [], rows: [] });
  const currentProyekRef = useRef(null);
  const abortControllerRef = useRef(null);

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  useEffect(() => {
    const userStorage = localStorage.getItem("user");
    if (userStorage) {
      const user = JSON.parse(userStorage);
      if (user.status === "user") {
        setIdPerusahaan(user.id_perusahaan);
      }
      setStatus(user.status);
    } else {
      logoutUser(navigate);
    }
  }, []);

  const fetchProyek = () => {
    setLoading(true);
    const url =
      status == "user"
        ? `${API_HOHOLOK}proyek/perusahaan/${id_perusahaan}/tahun/${tahun}`
        : `${API_HOHOLOK}proyek/tahun/${tahun}`;
    axios
      .get(url)
      .then((res) => {
        const data = res.data || [];
        const dataKU = data.map((item) => ({
          label: item.nama_proyek,
          value: item.id_proyek,
          ...item,
        }));
        setProyekList(dataKU);
        setSelectedProyek(dataKU[0]);

        setLoading(false);
      })
      .catch(() => {
        setProyekList([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (tahun && status) {
      fetchProyek();
    }
  }, [tahun, status]);
  // Fetch daftar proyek

  // Fetch data chart sesuai proyek & tahun
  useEffect(() => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Immediately reset state and set loading
    setChartData(null);
    setLoadingChart(true);

    // Update current proyek ref
    currentProyekRef.current = selectedProyek?.value;

    if (selectedProyek) {
      const proyekId = selectedProyek.value;

      // Ambil data target dari endpoint kurva-s (sama seperti kurva-s/index.js)
      axios
        .get(`${API_HOHOLOK}proyek/kurvas/${proyekId}`, {
          signal: abortControllerRef.current.signal,
        })
        .then((res) => {
          // Check if this response is still relevant (proyek hasn't changed)
          if (currentProyekRef.current !== proyekId) {
            return;
          }

          if (res.data && res.data.pekerjaan_utama && res.data.kurva_s) {
            const pekerjaanUtama = res.data.pekerjaan_utama || [];
            const kurvaS = res.data.kurva_s || [];

            // Ambil semua minggu unik dari data kurvaS
            const mingguList = [
              ...new Set(kurvaS.map((k) => k.minggu_ke)),
            ].sort((a, b) => a - b);

            // Hitung total progres per minggu (sama seperti kurva-s/index.js)
            const totPro = [];
            const labelGrap = [];
            let cumulativeTotal = 0; // Untuk menyimpan total kumulatif

            mingguList.forEach((minggu) => {
              let totalProgres = 0;
              pekerjaanUtama.forEach((utama) => {
                const kurva = kurvaS.find(
                  (k) =>
                    Number(k.id_pekerjaan_utama) ===
                      Number(utama.id_pekerjaan_utama) &&
                    Number(k.minggu_ke) === Number(minggu)
                );
                if (kurva) {
                  totalProgres += Number(kurva.progres_rencana) || 0;
                }
              });

              // Akumulasi progres: minggu 2 = minggu 1 + minggu 2, minggu 3 = minggu 1 + 2 + 3
              cumulativeTotal += totalProgres;

              totPro.push(cumulativeTotal);
              labelGrap.push(`Minggu ${minggu}`);
            });

            axios
              .get(
                `${API_HOHOLOK}proyek/realisasi-mingguan/${proyekId}?tahun=${tahun}`,
                { signal: abortControllerRef.current.signal }
              )
              .then((realisasiRes) => {
                // Check if this response is still relevant

                if (
                  realisasiRes.data &&
                  realisasiRes.data.success &&
                  Array.isArray(realisasiRes.data.data)
                ) {
                  const realisasiData = realisasiRes.data.data;

                  // Hitung total nilai proyek untuk normalisasi realisasi
                  const totalNilaiProyek = realisasiData.reduce(
                    (total, item) => {
                      return total + Number(item.akumulasi_nilai) || 0;
                    },
                    0
                  );

                  // Kelompokkan realisasi per minggu
                  const realisasiMap = {};
                  realisasiData.forEach((item) => {
                    const label = `Minggu ${item.target_minggu_ke}`;
                    if (!realisasiMap[label]) {
                      realisasiMap[label] = {
                        realisasi: 0,
                        nilai: 0,
                      };
                    }
                    realisasiMap[label].realisasi +=
                      Number(item.realisasi_nilai) || 0;
                    realisasiMap[label].nilai +=
                      Number(item.akumulasi_nilai) || 0;
                  });

                  // Hitung akumulasi realisasi mingguan
                  let cumulativeRealisasi = 0;
                  const akumulasiRealisasiPersen = [];

                  labelGrap.forEach((label) => {
                    const mingguData = realisasiMap[label] || {
                      realisasi: 0,
                      nilai: 0,
                    };

                    // Akumulasi nilai realisasi per minggu
                    cumulativeRealisasi += mingguData.realisasi;

                    // Hitung persentase realisasi berdasarkan total nilai proyek seluruhnya
                    const realisasiPersen =
                      totalNilaiProyek > 0
                        ? (cumulativeRealisasi / totalNilaiProyek) * 100
                        : 0;

                    akumulasiRealisasiPersen.push(realisasiPersen);
                  });

                  const chartPayload = {
                    labels: labelGrap,
                    datasets: [
                      {
                        label: "Akumulasi Target (%)",
                        color: "info",
                        data: totPro,
                      },
                      {
                        label: "Akumulasi Realisasi (%)",
                        color: "success",
                        data: akumulasiRealisasiPersen,
                      },
                    ],
                  };
                  setChartData(chartPayload);

                  // Tabel mingguan: Target akumulatif tampil penuh; Realisasi & Deviasi hanya sampai minggu terakhir realisasi tersedia
                  const availableWeeks = Object.keys(realisasiMap).map((l) =>
                    Number((l || "").toString().replace(/[^0-9]/g, ""))
                  );
                  const maxWeekWithData =
                    availableWeeks.length > 0
                      ? Math.max(...availableWeeks)
                      : -1;
                  const lastWeekIdx = labelGrap.findIndex(
                    (l) => l === `Minggu ${maxWeekWithData}`
                  );

                  const projectStart = selectedProyek?.tanggal_mulai
                    ? new Date(selectedProyek.tanggal_mulai)
                    : null;
                  const projectEnd = selectedProyek?.tanggal_selesai
                    ? new Date(selectedProyek.tanggal_selesai)
                    : null;

                  // Tentukan minggu yang berkaitan dengan current date
                  let highlightedIdx = -1;
                  if (projectStart instanceof Date && !isNaN(projectStart)) {
                    const today = new Date();
                    const msDiff = today.getTime() - projectStart.getTime();
                    const weekIdx = Math.floor(
                      msDiff / (1000 * 60 * 60 * 24 * 7)
                    );
                    const lastIdx = labelGrap.length - 1;
                    if (weekIdx < 0) highlightedIdx = 0;
                    else highlightedIdx = weekIdx > lastIdx ? lastIdx : weekIdx;
                  }

                  const weeklyRows = labelGrap.map((label, idx) => {
                    const targetCumulative = totPro[idx] || 0;
                    const hasRealData = lastWeekIdx >= 0 && idx <= lastWeekIdx;
                    const realCumulative = hasRealData
                      ? akumulasiRealisasiPersen[idx] || 0
                      : null;
                    const deviasi = hasRealData
                      ? (realCumulative || 0) - targetCumulative
                      : null;

                    let tanggalAwal = "-";
                    let tanggalAkhir = "-";
                    if (projectStart instanceof Date && !isNaN(projectStart)) {
                      const start = addDays(projectStart, idx * 7);
                      const end = addDays(projectStart, idx * 7 + 6);
                      const endCapped =
                        projectEnd && end > projectEnd ? projectEnd : end;
                      tanggalAwal = formatTanggalShort(start);
                      tanggalAkhir = formatTanggalShort(endCapped);
                    }

                    const deviasiCell =
                      deviasi === null ? (
                        "-"
                      ) : (
                        <span
                          style={{
                            color: deviasi < 0 ? "#d32f2f" : "#2e7d32",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontWeight: 600,
                          }}
                        >
                          {deviasi < 0 ? (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          )}
                          {`${deviasi.toFixed(2)}%`}
                        </span>
                      );

                    const mingguCell =
                      highlightedIdx === idx ? (
                        <span style={{ color: "#1a73e8", fontWeight: 700 }}>
                          {label}
                        </span>
                      ) : (
                        label
                      );

                    const rentangText = `${tanggalAwal} - ${tanggalAkhir}`;
                    const targetText = `${targetCumulative.toFixed(2)}%`;
                    const realisasiText =
                      realCumulative === null
                        ? "-"
                        : `${realCumulative.toFixed(2)}%`;

                    const toBlue = (content) => (
                      <span style={{ color: "#1a73e8", fontWeight: 600 }}>
                        {content}
                      </span>
                    );

                    return {
                      minggu: mingguCell,
                      rentang:
                        highlightedIdx === idx
                          ? toBlue(rentangText)
                          : rentangText,
                      target:
                        highlightedIdx === idx
                          ? toBlue(targetText)
                          : targetText,
                      realisasi:
                        highlightedIdx === idx
                          ? toBlue(realisasiText)
                          : realisasiText,
                      deviasi: deviasiCell,
                    };
                  });

                  setWeeklyTable({
                    columns: [
                      { Header: "Minggu", accessor: "minggu", width: "20%" },
                      {
                        Header: "Tanggal (Awal - Akhir)",
                        accessor: "rentang",
                        width: "30%",
                      },
                      {
                        Header: "Target (%)",
                        accessor: "target",
                        width: "16%",
                      },
                      {
                        Header: "Realisasi (%)",
                        accessor: "realisasi",
                        width: "16%",
                      },
                      {
                        Header: "Deviasi (%)",
                        accessor: "deviasi",
                        width: "18%",
                      },
                    ],
                    rows: weeklyRows,
                  });

                  // Debug logging
                } else {
                  // Jika tidak ada data realisasi, tampilkan hanya target
                  const chartPayload = {
                    labels: labelGrap,
                    datasets: [
                      {
                        label: "Akumulasi Target (%)",
                        color: "info",
                        data: totPro,
                      },
                    ],
                  };
                  setChartData(chartPayload);

                  // Tabel mingguan akumulatif hanya target (realisasi 0)
                  const projectStart = selectedProyek?.tanggal_mulai
                    ? new Date(selectedProyek.tanggal_mulai)
                    : null;
                  const projectEnd = selectedProyek?.tanggal_selesai
                    ? new Date(selectedProyek.tanggal_selesai)
                    : null;

                  // Tentukan minggu yang berkaitan dengan current date
                  let highlightedIdx = -1;
                  if (projectStart instanceof Date && !isNaN(projectStart)) {
                    const today = new Date();
                    const msDiff = today.getTime() - projectStart.getTime();
                    const weekIdx = Math.floor(
                      msDiff / (1000 * 60 * 60 * 24 * 7)
                    );
                    const lastIdx = labelGrap.length - 1;
                    if (weekIdx < 0) highlightedIdx = 0;
                    else highlightedIdx = weekIdx > lastIdx ? lastIdx : weekIdx;
                  }

                  const weeklyRows = labelGrap.map((label, idx) => {
                    const targetCumulative = totPro[idx] || 0;
                    const realCumulative = 0;
                    const deviasi = realCumulative - targetCumulative;

                    let tanggalAwal = "-";
                    let tanggalAkhir = "-";
                    if (projectStart instanceof Date && !isNaN(projectStart)) {
                      const start = addDays(projectStart, idx * 7);
                      const end = addDays(projectStart, idx * 7 + 6);
                      const endCapped =
                        projectEnd && end > projectEnd ? projectEnd : end;
                      tanggalAwal = formatTanggalShort(start);
                      tanggalAkhir = formatTanggalShort(endCapped);
                    }
                    const deviasiCell = (
                      <span
                        style={{
                          color: deviasi < 0 ? "#d32f2f" : "#2e7d32",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontWeight: 600,
                        }}
                      >
                        {deviasi < 0 ? (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        )}
                        {`${deviasi.toFixed(2)}%`}
                      </span>
                    );

                    const mingguCell =
                      highlightedIdx === idx ? (
                        <span style={{ color: "#1a73e8", fontWeight: 700 }}>
                          {label}
                        </span>
                      ) : (
                        label
                      );

                    const rentangText = `${tanggalAwal} - ${tanggalAkhir}`;
                    const targetText = `${targetCumulative.toFixed(2)}%`;
                    const realisasiText = `${realCumulative.toFixed(2)}%`;

                    const toBlue = (content) => (
                      <span style={{ color: "#1a73e8", fontWeight: 600 }}>
                        {content}
                      </span>
                    );

                    return {
                      minggu: mingguCell,
                      rentang:
                        highlightedIdx === idx
                          ? toBlue(rentangText)
                          : rentangText,
                      target:
                        highlightedIdx === idx
                          ? toBlue(targetText)
                          : targetText,
                      realisasi:
                        highlightedIdx === idx
                          ? toBlue(realisasiText)
                          : realisasiText,
                      deviasi: deviasiCell,
                    };
                  });
                  setWeeklyTable({
                    columns: [
                      { Header: "Minggu", accessor: "minggu", width: "20%" },
                      {
                        Header: "Tanggal (Awal - Akhir)",
                        accessor: "rentang",
                        width: "30%",
                      },
                      {
                        Header: "Target (%)",
                        accessor: "target",
                        width: "16%",
                      },
                      {
                        Header: "Realisasi (%)",
                        accessor: "realisasi",
                        width: "16%",
                      },
                      {
                        Header: "Deviasi (%)",
                        accessor: "deviasi",
                        width: "18%",
                      },
                    ],
                    rows: weeklyRows,
                  });
                }
              })
              .catch((error) => {
                // Only log error if it's not an abort error
                if (error.name !== "AbortError") {
                  console.error("Error fetching realisasi data:", error);
                  console.error("Error response:", error.response?.data);
                }

                // Check if this error is still relevant
                if (currentProyekRef.current === proyekId) {
                  // Jika gagal ambil realisasi, tampilkan hanya target
                  setChartData({
                    labels: labelGrap,
                    datasets: [
                      {
                        label: "Akumulasi Target (%)",
                        color: "info",
                        data: totPro,
                      },
                    ],
                  });
                }
              });
          } else {
            setChartData(null);
          }
        })
        .catch((error) => {
          // Only log error if it's not an abort error
          if (error.name !== "AbortError") {
            console.error("Error fetching kurva S data:", error);
          }

          // Check if this error is still relevant
          if (currentProyekRef.current === proyekId) {
            setChartData(null);
          }
        })
        .finally(() => {
          // Only update loading if this is still the current request
          if (currentProyekRef.current === proyekId) {
            setLoadingChart(false);
          }
        });
    } else {
      setLoadingChart(false);
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedProyek, tahun]);

  // useLayoutEffect for immediate state reset before render
  useLayoutEffect(() => {
    if (selectedProyek) {
      setChartData(null);
      setLoadingChart(true);
    }
  }, [selectedProyek?.value]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox mb={2} display="flex" alignItems="center" gap={2}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={proyekList}
                    value={selectedProyek}
                    onChange={(event, newValue) => {
                      setSelectedProyek(newValue);
                    }}
                    loading={loading}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pilih Proyek"
                        variant="standard"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    sx={{ minWidth: 250 }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Autocomplete
                    options={[2024, 2025, 2026, 2027, 2028, 2029]}
                    value={tahun}
                    onChange={(event, newValue) => {
                      setTahun(newValue);
                    }}
                    getOptionLabel={(option) => option.toString()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tahun"
                        variant="standard"
                        fullWidth
                      />
                    )}
                    sx={{ minWidth: 120 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </MDBox>
        {selectedProyek && (
          <MDBox mt={1} p={3} borderRadius={2} bgcolor="#f0f4fa" boxShadow={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  color="info.main"
                  mb={1}
                >
                  {selectedProyek.label}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Lokasi:</b> {selectedProyek.lokasi || "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Desa:</b> {selectedProyek.desa || "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Koordinat:</b>{" "}
                  {selectedProyek.latitude && selectedProyek.longitude
                    ? `${selectedProyek.latitude}, ${selectedProyek.longitude}`
                    : "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="body2" color="text">
                  <b>Tanggal Mulai:</b>{" "}
                  {formatTanggalIndo(selectedProyek.tanggal_mulai)}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Tanggal Selesai:</b>{" "}
                  {formatTanggalIndo(selectedProyek.tanggal_selesai)}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Konsultan Perencana:</b>{" "}
                  {selectedProyek.nama_konsultan_perencana ||
                    selectedProyek.konsultan_perencana?.nama ||
                    "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Kontraktor Pelaksana:</b>{" "}
                  {selectedProyek.nama_kontraktor_pelaksana ||
                    selectedProyek.kontraktor_pelaksana?.nama ||
                    "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Konsultan Pengawas:</b>{" "}
                  {selectedProyek.nama_konsultan_pengawas ||
                    selectedProyek.konsultan_pengawas?.nama ||
                    "-"}
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>
        )}
        <MDBox mb={6}>
          {loadingChart ? (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress color="info" />
              <MDTypography variant="body1" ml={2} color="text.secondary">
                Memuat data chart...
              </MDTypography>
            </MDBox>
          ) : !chartData ? (
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              py={6}
              textAlign="center"
            >
              <MDTypography variant="h6" color="text.secondary" mb={1}>
                Tidak ada data chart
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Belum ada data realisasi mingguan untuk proyek ini pada tahun{" "}
                {tahun}
              </MDTypography>
            </MDBox>
          ) : (
            <Grid
              container
              spacing={3}
              key={`chart-${selectedProyek?.value}-${tahun}-${
                chartData ? "has-data" : "no-data"
              }`}
            >
              <Grid item xs={12}>
                <GradientLineChart
                  icon={{ component: "show_chart" }}
                  title="Akumulasi Target vs Realisasi Mingguan"
                  height="20rem"
                  description="Akumulasi target dan realisasi seluruh pekerjaan utama per minggu"
                  chart={chartData}
                />
              </Grid>
              {weeklyTable?.rows?.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3} lineHeight={1}>
                      <MDTypography variant="h6" fontWeight="medium">
                        Tabel Target & Realisasi Mingguan (beserta Deviasi)
                      </MDTypography>
                      <MDTypography variant="button" color="text">
                        Proyek {selectedProyek?.label} - Tahun {tahun}
                      </MDTypography>
                    </MDBox>
                    <DataTable
                      table={{
                        columns: weeklyTable.columns,
                        rows: weeklyTable.rows,
                      }}
                      entriesPerPage={{ defaultValue: 100, values: [100] }}
                    />
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default KurvaTargetRealisasi;
